/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { AccordionGroup, Divider, Grid, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';
import type { AccordionGroupProps } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import useIsMobile from 'bundles/unified-common/hooks/useIsMobile';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import NewAiBadge from 'bundles/unified-description-page-common/components/shared/badges/NewAiBadge';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

interface Props {
  title: string | React.ReactNode;
  syllabusItems: AccordionGroupProps['children'];
  sidebar?: React.ReactNode;
  anchorId?: string;
  footer?: React.ReactNode;
  slug?: string;
}

const styles = {
  title: css`
    margin-bottom: calc(var(--cds-spacing-100) * -1);
  `,
  description: css`
    margin-top: var(--cds-spacing-300);

    ${breakpoints.up('sm')} {
      padding-bottom: var(--cds-spacing-100);
    }
  `,
  syllabusContent: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    overflow: hidden;
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  // This is a hack to prevent the underline from appearing on the accordion button when it is focused
  // This state class is managed by the CDS accordion component and for an unknwon reason styling specificity rules
  // don't work as intended. Having it here works fine.
  accordionGroup: css`
    padding: 0;
    margin: 0;

    .cds-button-focusVisible {
      text-decoration: none !important;
    }
  `,
  sidebar: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-200);
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
      padding: 0 var(--cds-spacing-200);
    }
  `,
  footer: css`
    &:has(> *) {
      display: flex;
      flex-direction: column;
      margin: 0;
      gap: var(--cds-spacing-200);
      padding: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
    }
  `,
  badgeSpacing: css`
    /* Handle LTR and RTL */
    display: inline-block;
    padding-left: var(--cds-spacing-100);
    padding-right: var(--cds-spacing-100);
  `,
  divider: css`
    margin: 0 var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);
    height: 2px;
    background-color: var(--cds-color-grey-50);

    ${breakpoints.down('xs')} {
      margin: 0 0 var(--cds-spacing-200) 0;
    }
  `,
};

function SyllabusModule({ title, syllabusItems, sidebar, anchorId, footer, slug }: Props) {
  const isMobile = useIsMobile();
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'syllabus',
      },
      ...eventingProductData,
    }
  );

  const items = useMemo(() => {
    if (!syllabusItems || !(Array.isArray(syllabusItems) && syllabusItems.length)) {
      return null;
    }
    return syllabusItems.map((item) => item);
  }, [syllabusItems]);

  if (!items) {
    return null;
  }

  return (
    <Section anchorId={anchorId} ref={visibilityTrackingRef}>
      <PageGridContainer>
        <Grid item container spacing={16}>
          <Grid item xs={12}>
            <Typography2 variant="subtitleLarge" component="h2" css={styles.title}>
              {title}
              <div css={styles.badgeSpacing}>
                <NewAiBadge slug={slug} />
              </div>
            </Typography2>
          </Grid>
          <Grid item xs={12} md={9} sm={8}>
            <TrackedDiv
              trackingName="syllabus"
              trackClicks={false}
              requireFullyVisible={false}
              withVisibilityTracking={true}
            >
              <div css={styles.syllabusContent}>
                {isMobile ? (
                  <React.Fragment>{items}</React.Fragment>
                ) : (
                  <AccordionGroup css={styles.accordionGroup} multiExpand spacing={8}>
                    {items}
                  </AccordionGroup>
                )}
                {footer && (
                  <React.Fragment>
                    <Divider css={styles.divider} />
                    <div css={styles.footer}>{footer}</div>
                  </React.Fragment>
                )}
              </div>
            </TrackedDiv>
          </Grid>
          <Grid item xs={12} md={3} sm={4}>
            {sidebar && <div css={styles.sidebar}>{sidebar}</div>}
          </Grid>
        </Grid>
      </PageGridContainer>
    </Section>
  );
}

export default SyllabusModule;
